import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { DeviceInfo, UploadOptions, IOSSafariLimitations } from '../types';

const DEBUG_DEVICE_LOGS = process.env.REACT_APP_DEBUG_DEVICE === 'true';

interface DeviceContextType {
  deviceInfo: DeviceInfo;
  uploadOptions: UploadOptions;
  iosSafariLimitations: IOSSafariLimitations;
  memoryUsage: any;
  updateMemoryUsage: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const [deviceInfo] = useState<DeviceInfo>(() => detectDevice());
  const [memoryUsage, setMemoryUsage] = useState<any>(null);

  function detectDevice(): DeviceInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Enhanced iOS Safari detection
    const isIOSSafari = /iphone|ipad|ipod/.test(userAgent) && 
                       /safari/.test(userAgent) && 
                       !/chrome|crios|fxios|edgios/.test(userAgent);
    
    const isMobile = /mobile|iphone|ipad|ipod|android|blackberry|mini|windows\sce|palm/i.test(userAgent);
    
    // Device-specific limitations
    const maxRecommendedFileSize = isIOSSafari ? 25 * 1024 * 1024 : 50 * 1024 * 1024; // 25MB for iOS Safari
    const maxRecommendedBatchSize = isIOSSafari ? 10 : 50; // Smaller batches for iOS Safari
    
    if (DEBUG_DEVICE_LOGS) {
      console.log('🔍 Device Detection:', {
        isIOSSafari,
        isMobile,
        userAgent: navigator.userAgent,
        maxRecommendedFileSize: `${maxRecommendedFileSize / 1024 / 1024}MB`,
        maxRecommendedBatchSize
      });
    }

    return {
      isIOSSafari,
      isMobile,
      userAgent: navigator.userAgent,
      maxRecommendedFileSize,
      maxRecommendedBatchSize,
      supportsImageCompression: 'createImageBitmap' in window,
      supportsChunkedUpload: 'fetch' in window && 'ReadableStream' in window,
    };
  }

  const uploadOptions: UploadOptions = useMemo(() => ({
    maxFileSize: deviceInfo.maxRecommendedFileSize,
    maxBatchSize: deviceInfo.maxRecommendedBatchSize,
    compressionQuality: deviceInfo.isIOSSafari ? 0.7 : 0.8, // More aggressive compression for iOS
    compressionMaxWidth: deviceInfo.isIOSSafari ? 2048 : 4096,
    compressionMaxHeight: deviceInfo.isIOSSafari ? 2048 : 4096,
    enableChunkedUpload: deviceInfo.supportsChunkedUpload && deviceInfo.isIOSSafari,
    chunkSize: 1024 * 1024, // 1MB chunks
    maxConcurrentUploads: deviceInfo.isIOSSafari ? 2 : 4,
    retryAttempts: 4,
  }), [deviceInfo]);

  const iosSafariLimitations: IOSSafariLimitations = {
    maxFileSize: 50 * 1024 * 1024, // 50MB hard limit
    maxTotalSize: 200 * 1024 * 1024, // 200MB total
    maxFileCount: 50,
    requiresCompression: true,
    memoryConstraints: {
      maxHeapSize: 1024 * 1024 * 1024, // 1GB estimated limit
      recommendedBatchSize: 5, // Very conservative for memory
    },
  };

  const updateMemoryUsage = useCallback(() => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      setMemoryUsage({
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        timestamp: Date.now(),
      });
      
      // Log memory usage for iOS Safari debugging
      if (deviceInfo.isIOSSafari) {
        const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
        const totalMB = Math.round(memory.totalJSHeapSize / 1024 / 1024);
        const limitMB = Math.round(memory.jsHeapSizeLimit / 1024 / 1024);
        
        if (DEBUG_DEVICE_LOGS) {
          console.log(`🧠 iOS Safari Memory: ${usedMB}MB / ${totalMB}MB (limit: ${limitMB}MB)`);
        }
        
        // Warn if memory usage is high
        if (memory.usedJSHeapSize / memory.jsHeapSizeLimit > 0.8) {
          console.warn('⚠️ iOS Safari: High memory usage detected!');
        }
      }
    }
  }, [deviceInfo.isIOSSafari]);

  useEffect(() => {
    // Initial memory check
    updateMemoryUsage();
    
    // Periodic memory monitoring for iOS Safari
    if (deviceInfo.isIOSSafari) {
      const interval = setInterval(updateMemoryUsage, 10000); // Every 10 seconds
      return () => clearInterval(interval);
    }
  }, [deviceInfo.isIOSSafari, updateMemoryUsage]);

  // Log device-specific optimizations
  useEffect(() => {
    if (deviceInfo.isIOSSafari) {
      if (DEBUG_DEVICE_LOGS) {
        console.log('📱 iOS Safari Optimizations Active:');
        console.log(`  - Max file size: ${uploadOptions.maxFileSize / 1024 / 1024}MB`);
        console.log(`  - Max batch size: ${uploadOptions.maxBatchSize} files`);
        console.log(`  - Compression quality: ${uploadOptions.compressionQuality * 100}%`);
        console.log(`  - Max concurrent uploads: ${uploadOptions.maxConcurrentUploads}`);
        console.log(`  - Chunked upload: ${uploadOptions.enableChunkedUpload ? 'enabled' : 'disabled'}`);
      }
    }
  }, [deviceInfo.isIOSSafari, uploadOptions]);

  const contextValue: DeviceContextType = {
    deviceInfo,
    uploadOptions,
    iosSafariLimitations,
    memoryUsage,
    updateMemoryUsage,
  };

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
};
